import React from 'react';
import { Star, Shield, Users, Award, CheckCircle, Heart, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600">
      {/* Hero Section */}
      <div className="relative h-[500px] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-400/50 via-purple-500/50 to-indigo-600/50"></div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6 text-white">
                About Psychic Insights
              </h1>
              <p className="text-xl text-white mb-8">
                Your trusted destination for authentic psychic readings, spiritual guidance, and personal transformation
              </p>
              <div className="flex justify-center gap-8">
                <div className="flex items-center bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <Star className="text-yellow-400 w-6 h-6 mr-2" />
                  <span className="text-white">Top Rated</span>
                </div>
                <div className="flex items-center bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <Shield className="text-white w-6 h-6 mr-2" />
                  <span className="text-white">Verified Experts</span>
                </div>
                <div className="flex items-center bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <Users className="text-white w-6 h-6 mr-2" />
                  <span className="text-white">Global Community</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Statement */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg shadow-xl p-8 md:p-12 text-white">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg text-purple-100 leading-relaxed mb-8">
                At Psychic Insights, we're dedicated to connecting seekers with authentic psychic advisors who can provide 
                genuine guidance and clarity. Our mission is to create a safe, trustworthy space where you can explore life's 
                deeper questions and find the insights you need to move forward with confidence.
              </p>
              <div className="flex flex-wrap justify-center gap-8">
                <div className="flex items-center text-purple-200">
                  <Shield className="w-6 h-6 mr-2" />
                  <span className="font-medium">Verified Advisors</span>
                </div>
                <div className="flex items-center text-purple-200">
                  <Heart className="w-6 h-6 mr-2" />
                  <span className="font-medium">Genuine Guidance</span>
                </div>
                <div className="flex items-center text-purple-200">
                  <Users className="w-6 h-6 mr-2" />
                  <span className="font-medium">Supportive Community</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            Why Choose Psychic Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 text-white">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Expert Advisors</h3>
              <p className="text-purple-100">
                Our rigorous screening process ensures only the most gifted and authentic psychic advisors join our platform.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 text-white">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">100% Satisfaction</h3>
              <p className="text-purple-100">
                We stand behind our services with a satisfaction guarantee and 24/7 customer support.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 text-white">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Industry Leaders</h3>
              <p className="text-purple-100">
                Recognized for our commitment to quality, authenticity, and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg shadow-xl p-8 md:p-12 text-white">
            <h2 className="text-3xl font-bold text-center mb-12">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Authenticity</h3>
                  <p className="text-purple-100">
                    We believe in genuine connections and authentic readings that provide real value and insight.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Trust & Safety</h3>
                  <p className="text-purple-100">
                    Your privacy and security are our top priorities, with encrypted communications and secure transactions.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Compassion</h3>
                  <p className="text-purple-100">
                    We approach every reading with empathy, understanding, and genuine care for our clients.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-full mr-4">
                  <Star className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Excellence</h3>
                  <p className="text-purple-100">
                    We continuously strive to provide the highest quality psychic readings and customer service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-white mb-6">Begin Your Journey Today</h2>
            <p className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto">
              Experience the difference with Psychic Insights. Connect with our gifted advisors and discover the clarity you seek.
            </p>
            <button 
              onClick={() => navigate('/signup')}
              className="bg-purple-600 text-white px-8 py-3 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold"
            >
              Get Your First Reading
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;